import Backbone from 'backbone';
import SearchSubmenu from './search-submenu';
import { TourStopSearchForm, tourHeaderSelectorMobile } from './tour-stop-search-form';
import { reflow } from './util';
import { getFocusables, onTabKeyDownCallback } from '../utils/header-focusable';

const HeaderMobile = Backbone.View.extend({
  initialize() {
    this.menu          = new MenuMobile({ el: '[data-header]' });
    this.primaryNav    = new PrimaryNavMobile({ el: '[data-header-mobile-menu]' });
    this.searchSubmenu = new SearchSubmenu({ el: '[data-header-mobile] [data-search-submenu-form]' });

    if (document.querySelector(tourHeaderSelectorMobile)) {
      this.tourStopSearchForm = new TourStopSearchForm({ el: tourHeaderSelectorMobile });
    }
  },
  dispose() {
    this.menu.dispose();
    this.primaryNav.dispose();
    this.searchSubmenu.dispose();
    if (this.tourStopSearchForm) this.tourStopSearchForm.dispose();

    this.menu = null;
    this.primaryNav = null;
    this.searchSubmenu = null;
    if (this.tourStopSearchForm) this.tourStopSearchForm = null;
  },
});

const MenuMobile = Backbone.View.extend({
  events: {
    "click [data-mobile-menu-trigger]": "onClickTrigger",
  },
  initialize() {
    this.initListeners();
  },
  initListeners() {
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.listenTo(Backbone, {
      'primary-nav-mobile:submenu:open':   this.openSubmenu,
      'primary-nav-mobile:submenu:close':  this.closeSubmenu,
    });
  },
  isMenuOpen() {
    return this.el.getAttribute('data-mobile-menu-expanded') === 'true';
  },
  onClickTrigger() {
    if (this.isMenuOpen()) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  },
  openMenu() {
    this.el.setAttribute('data-mobile-menu-expanded', true);
    document.body.classList.add('layout/overflow:hidden');
    this.focusables = getFocusables(this.el);

    document.addEventListener('keydown', this.boundOnKeyDown);
    Backbone.trigger('primary-nav:mobile-menu:open');
  },
  closeMenu() {
    this.el.removeAttribute('data-mobile-menu-expanded');
    document.body.classList.remove('layout/overflow:hidden');
    this.focusables = [];

    document.removeEventListener('keydown', this.boundOnKeyDown);
    Backbone.trigger('primary-nav:mobile-menu:close');
  },
  openSubmenu() {
    this.el.setAttribute('data-submenu-expanded', true);
    document.removeEventListener('keydown', this.boundOnKeyDown);
  },
  closeSubmenu() {
    this.el.removeAttribute('data-submenu-expanded');
    document.addEventListener('keydown', this.boundOnKeyDown);
  },
  onKeyDown(e) {
    switch (e.key) {
      case 'Tab':
        if (this.isMenuOpen() && this.focusables) onTabKeyDownCallback(e, this.focusables, this.closeMenu.bind(this));
        break;
      case 'Escape':
        this.closeMenu();
        break;
    }
  },
  dispose() {
    this.closeMenu();
    this.undelegateEvents();
    this.stopListening();
  },
});

const PrimaryNavMobile = Backbone.View.extend({
  events: {
    "click [data-submenu-trigger]": "onClickSubmenuTrigger",
    "click [data-back-button]":     "onClickBackButton",
  },
  initialize() {
    this.initListeners();
  },
  initListeners() {
    this.listenTo(Backbone, {'primary-nav:mobile-menu:close': this.closeSubmenu});
    this.boundOnFadeInTransitionEnd = this.onFadeInTransitionEnd.bind(this);
    this.boundOnFadeOutTransitionEnd = this.onFadeOutTransitionEnd.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
  },
  isMenuOpen() {
    return this.el.querySelectorAll('[data-submenu-trigger][aria-expanded="true"]').length > 0;
  },
  onClickSubmenuTrigger(e) {
    this.submenuTrigger = e.currentTarget;
    this.submenu        = this.submenuTrigger.nextElementSibling;

    if (this.submenuTrigger.getAttribute('aria-expanded') === 'true') {
      this.closeSubmenu(e);
    } else {
      this.openSubmenu(e);
    }
  },
  onClickBackButton() {
    this.closeSubmenu();
  },
  toggleTriggersHidden(value) {
    this.el.querySelectorAll('[data-submenu-trigger], [data-submenu-link]').forEach(trigger => {
      if (value === true) {
        trigger.classList.add('hidden');
      } else {
        trigger.classList.remove('hidden');
      }
    });
  },
  openSubmenu() {
    if (this.submenu && this.submenuTrigger) {
      this.clearTransitionEndListeners();

      this.submenu.style.display = 'block';
      reflow(this.submenu);
      this.submenu.classList.add('visible');
      this.submenuTrigger.setAttribute('aria-expanded', true);

      this.submenu.addEventListener('transitionend', this.boundOnFadeInTransitionEnd);
      document.addEventListener('keydown', this.boundOnKeyDown);

      Backbone.trigger('primary-nav-mobile:submenu:open');
    }
  },
  closeSubmenu() {
    if (this.submenu && this.submenuTrigger) {
      this.clearTransitionEndListeners();

      this.toggleTriggersHidden(false);
      this.submenuTrigger.removeAttribute('aria-expanded');
      this.submenu.addEventListener('transitionend', this.boundOnFadeOutTransitionEnd);
      this.submenu.classList.remove('visible');
      this.focusables = [];

      document.removeEventListener('keydown', this.boundOnKeyDown);

      Backbone.trigger('primary-nav-mobile:submenu:close');
    }
  },
  resetAllSubmenus() {
    document.querySelectorAll('[data-submenu]').forEach((el) => {
      el.style.display = '';
    });
  },
  clearTransitionEndListeners() {
    if (this.submenu) {
      this.submenu.removeEventListener('transitionend', this.boundOnFadeInTransitionEnd);
      this.submenu.removeEventListener('transitionend', this.boundOnFadeOutTransitionEnd);
    }
  },
  onFadeInTransitionEnd(e) {
    if (e.propertyName === 'opacity') {
      this.focusables = getFocusables(this.el);
      if (this.focusables.length > 0) this.focusables[0].focus();
    }
    this.toggleTriggersHidden(true);
    if (this.submenu) this.submenu.removeEventListener('transitionend', this.boundOnFadeInTransitionEnd);
  },
  onFadeOutTransitionEnd() {
    if (this.submenu) {
      this.submenu.removeEventListener('transitionend', this.boundOnFadeOutTransitionEnd);
      this.submenu.style.display = 'none';
    }
    this.resetAllSubmenus();
  },
  onKeyDown(e) {
    switch (e.key) {
      case 'Tab':
        if (this.isMenuOpen() && this.focusables) onTabKeyDownCallback(e, this.focusables, this.closeSubmenu.bind(this));
        break;
      case 'Escape':
        this.closeSubmenu();
        break;
    }
  },
  dispose() {
    this.closeSubmenu();
    this.resetAllSubmenus();
    this.clearTransitionEndListeners();
    this.undelegateEvents();
    this.stopListening();
  },
});

export default HeaderMobile;
